import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Globe, Loader2, ExternalLink, Filter } from 'lucide-react';
import {
  resolutionApi,
  type GlobalCanonicalDetail,
  type GlobalCanonicalSummary,
} from '@/services/api';
import { useKnowledgeStore } from '@/stores/knowledge';

export function GlobalConceptsTab() {
  const [items, setItems] = useState<GlobalCanonicalSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [entityType, setEntityType] = useState<string>('');
  const [minClients, setMinClients] = useState<number>(2);
  const [selected, setSelected] = useState<GlobalCanonicalDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { entityTypes, fetchEntityTypes } = useKnowledgeStore();

  useEffect(() => {
    fetchEntityTypes();
  }, [fetchEntityTypes]);

  useEffect(() => {
    setLoading(true);
    resolutionApi
      .listGlobalCanonicals({
        entity_type: entityType || undefined,
        min_client_count: minClients,
        limit: 200,
      })
      .then((res) => setItems(res.global_canonicals))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [entityType, minClients]);

  const handleSelect = (id: string) => {
    setDetailLoading(true);
    resolutionApi
      .getGlobalCanonical(id)
      .then(setSelected)
      .catch(() => setSelected(null))
      .finally(() => setDetailLoading(false));
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
        <Filter size={12} className="text-slate-400" />
        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"
        >
          <option value="">All entity types</option>
          {entityTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-xs text-slate-500">
          Min clients
          <input
            type="number"
            min={1}
            max={20}
            step={1}
            value={minClients}
            onChange={(e) => setMinClients(Number(e.target.value) || 2)}
            className="w-16 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1"
          />
        </label>
        {loading && <Loader2 size={12} className="animate-spin text-slate-400 ml-auto" />}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-5 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60">
            <h3 className="text-xs font-semibold text-navy-900 flex items-center gap-1.5">
              <Globe size={12} className="text-brand-teal" />
              Shared concepts ({items.length})
            </h3>
          </div>
          <div className="max-h-[60vh] overflow-auto divide-y divide-slate-100">
            {items.length === 0 && !loading ? (
              <div className="px-4 py-8 text-xs text-slate-400 text-center">
                No global canonicals yet. Run cross-client resolution to populate.
              </div>
            ) : (
              items.map((g) => (
                <GlobalRow
                  key={g.id}
                  item={g}
                  selected={selected?.id === g.id}
                  onClick={() => handleSelect(g.id)}
                />
              ))
            )}
          </div>
        </div>
        <div className="col-span-7 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <GlobalDetail detail={selected} loading={detailLoading} />
        </div>
      </div>
    </div>
  );
}

function GlobalRow({
  item,
  selected,
  onClick,
}: {
  item: GlobalCanonicalSummary;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-4 py-3 transition-colors',
        selected
          ? 'bg-brand-teal/5 border-l-4 border-l-brand-teal'
          : 'hover:bg-slate-50 border-l-4 border-l-transparent',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-800 truncate flex-1">
          {item.global_name}
        </span>
        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border bg-brand-teal/10 text-brand-teal-dark border-brand-teal/20 flex-shrink-0">
          {item.client_count} clients
        </span>
      </div>
      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
        <span>{item.entity_type}</span>
        {item.ontological_class && <span>· {item.ontological_class}</span>}
        <span>· {item.canonical_member_count} members</span>
      </div>
    </button>
  );
}

function GlobalDetail({
  detail,
  loading,
}: {
  detail: GlobalCanonicalDetail | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-slate-400 p-8">
        <Loader2 size={14} className="animate-spin inline mr-2" />
        Loading detail...
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-slate-400 p-8">
        Select a global concept to view cross-client members
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-navy-900">{detail.global_name}</h3>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
          <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
            {detail.entity_type}
          </span>
          {detail.ontological_class && (
            <span>{detail.ontological_class}</span>
          )}
          <span>{detail.client_count} clients</span>
          <span>{detail.members.length} members</span>
        </div>
      </div>
      <div className="flex-1 overflow-auto px-5 py-4">
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
          Client canonicals
        </div>
        <div className="space-y-1">
          {detail.members.map((m) => (
            <Link
              key={m.id}
              to={`/knowledge/estate/entities/${m.id}`}
              className="flex items-center gap-3 text-xs border border-slate-200 rounded-lg px-3 py-2 hover:border-brand-teal/50 hover:bg-brand-teal/5 transition-colors"
            >
              <span className="text-slate-700 truncate flex-1">
                {m.canonical_name}
              </span>
              {m.client_id && (
                <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px]">
                  {m.client_id}
                </span>
              )}
              <span className="text-[10px] text-slate-500">
                {m.source_entity_count} sources
              </span>
              <span className="text-[10px] text-emerald-600 font-semibold">
                {(m.confidence * 100).toFixed(0)}%
              </span>
              <ExternalLink size={11} className="text-slate-400" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
